import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Globe } from 'lucide-react';
import { useLanguage } from './LanguageContext';
// Generate static particles outside component to avoid hydration/re-render shifts
const PARTICLES = Array.from({
  length: 30
}).map((_, i) => ({
  id: i,
  size: Math.random() * 3 + 1,
  x: Math.random() * 100,
  y: Math.random() * 100,
  duration: Math.random() * 15 + 15,
  delay: Math.random() * 5,
  isGold: Math.random() > 0.5
}));
export function WelcomePage() {
  const {
    selectLanguage
  } = useLanguage();
  return <div className="min-h-screen bg-[#030303] flex items-center justify-center relative overflow-hidden font-sans" data-id="element-266">
      {/* --- BACKGROUND IMAGE --- */}
      <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
      backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/debkG9NMCNAnG67vQPGGTL/hurricane-season-mid-year-outlook-stage.webp")'
    }} data-id="element-267" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#030303]/85 to-[#030303]" data-id="element-268" />

      {/* --- AMBIENT BACKGROUND EFFECTS --- */}

      {/* Subtle Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,#000_40%,transparent_100%)] pointer-events-none" data-id="element-269" />

      {/* Glowing Orbs */}
      <motion.div className="absolute top-[-10%] left-[-10%] w-[40rem] h-[40rem] bg-brand-red/10 rounded-full blur-[120px] pointer-events-none" animate={{
      scale: [1, 1.2, 1],
      opacity: [0.3, 0.5, 0.3]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-270" />
      <motion.div className="absolute bottom-[-10%] right-[-10%] w-[40rem] h-[40rem] bg-brand-gold/10 rounded-full blur-[120px] pointer-events-none" animate={{
      scale: [1, 1.3, 1],
      opacity: [0.2, 0.4, 0.2]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} data-id="element-271" />

      {/* Scanning Line */}
      <motion.div className="absolute left-0 right-0 h-[1px] bg-brand-red/30 shadow-[0_0_15px_rgba(220,38,38,0.5)] pointer-events-none z-0" animate={{
      top: ['-10%', '110%']
    }} transition={{
      duration: 6,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-272" />

      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none z-0" data-id="element-273">
        {PARTICLES.map(p => <motion.div key={p.id} className={`absolute rounded-full ${p.isGold ? 'bg-brand-gold' : 'bg-brand-red'} blur-[1px]`} style={{
        width: p.size,
        height: p.size,
        left: `${p.x}%`,
        top: `${p.y}%`
      }} animate={{
        y: [0, -150],
        opacity: [0, 0.6, 0]
      }} transition={{
        duration: p.duration,
        repeat: Infinity,
        delay: p.delay,
        ease: 'linear'
      }} data-id="element-274" />)}
      </div>

      {/* --- MAIN CONTENT --- */}
      <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-275">
        {/* Logo Reveal */}
        <motion.div initial={{
        opacity: 0,
        scale: 1.5,
        filter: 'blur(20px)'
      }} animate={{
        opacity: 1,
        scale: 1,
        filter: 'blur(0px)'
      }} transition={{
        duration: 1.5,
        ease: 'easeOut',
        delay: 0.2
      }} className="relative mb-10" data-id="element-276">
          {/* Continuous Logo Glow */}
          <motion.div className="absolute inset-0 bg-brand-gold/20 blur-[40px] rounded-full" animate={{
          opacity: [0.4, 0.7, 0.4],
          scale: [0.8, 1.1, 0.8]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-277" />
          <img src="https://cdn.magicpatterns.com/uploads/ocQwAnLv8ZRS6ZM4MrGo2A/LOGO_-_ELITE_-_DORADO.png" alt="Elite Property Adjusters LLC Logo" className="relative z-10 h-44 md:h-56 w-auto mx-auto drop-shadow-[0_0_25px_rgba(245,158,11,0.2)]" data-id="element-278" />
        </motion.div>

        {/* Animated Divider */}
        <motion.div initial={{
        scaleX: 0,
        opacity: 0
      }} animate={{
        scaleX: 1,
        opacity: 1
      }} transition={{
        delay: 1.2,
        duration: 1,
        ease: 'easeInOut'
      }} className="w-32 h-[1px] bg-gradient-to-r from-transparent via-brand-gold to-transparent mb-8" data-id="element-279" />

        {/* Tagline */}
        <motion.div className="overflow-hidden mb-16" data-id="element-280">
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.6,
          duration: 0.8,
          ease: 'easeOut'
        }} className="text-gray-300 text-sm md:text-base tracking-[0.3em] uppercase font-light" data-id="element-281">
            Maximizing Your Insurance Claim Settlement
          </motion.p>
        </motion.div>

        {/* Language Selection Area */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 2.2,
        duration: 1
      }} className="w-full flex flex-col items-center" data-id="element-282">
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-283">
            <Globe className="text-brand-gold animate-pulse" size={18} data-id="element-284" />
            <span className="text-brand-gold/80 text-xs md:text-sm font-medium uppercase tracking-[0.2em]" data-id="element-285">
              Select System Language / Seleccione Idioma
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-6 justify-center w-full max-w-xl" data-id="element-286">
            {/* English Button */}
            <motion.button initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 2.5,
            duration: 0.6,
            ease: 'easeOut'
          }} onClick={() => selectLanguage('en')} className="group relative px-8 py-5 w-full sm:w-64 bg-black/40 backdrop-blur-md border border-white/10 overflow-hidden transition-all duration-500 hover:border-brand-gold/50 hover:shadow-[0_0_30px_rgba(245,158,11,0.15)]" data-id="element-287">
              {/* Sci-fi Corner Brackets */}
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-brand-gold/30 group-hover:border-brand-gold transition-colors duration-300" data-id="element-288" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-brand-gold/30 group-hover:border-brand-gold transition-colors duration-300" data-id="element-289" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-brand-gold/30 group-hover:border-brand-gold transition-colors duration-300" data-id="element-290" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-brand-gold/30 group-hover:border-brand-gold transition-colors duration-300" data-id="element-291" />

              {/* Hover Fill Glow */}
              <div className="absolute inset-0 bg-brand-gold/0 group-hover:bg-brand-gold/5 transition-colors duration-500" data-id="element-292" />

              {/* Sweeping Light Reflection */}
              <div className="absolute top-0 left-[-100%] w-[50%] h-full bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 group-hover:left-[200%] transition-all duration-1000 ease-in-out" data-id="element-293" />

              <div className="relative flex items-center justify-center gap-4" data-id="element-294">
                <img src="https://flagcdn.com/w40/us.png" alt="US Flag" className="w-8 h-6 object-cover rounded-sm shadow-[0_0_8px_rgba(255,255,255,0.2)]" data-id="element-295" />
                <span className="uppercase tracking-[0.2em] text-sm font-medium text-gray-400 group-hover:text-white transition-colors duration-300" data-id="element-296">
                  English
                </span>
              </div>
            </motion.button>

            {/* Spanish Button */}
            <motion.button initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 2.6,
            duration: 0.6,
            ease: 'easeOut'
          }} onClick={() => selectLanguage('es')} className="group relative px-8 py-5 w-full sm:w-64 bg-black/40 backdrop-blur-md border border-white/10 overflow-hidden transition-all duration-500 hover:border-brand-gold/50 hover:shadow-[0_0_30px_rgba(245,158,11,0.15)]" data-id="element-297">
              {/* Sci-fi Corner Brackets */}
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-brand-gold/30 group-hover:border-brand-gold transition-colors duration-300" data-id="element-298" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-brand-gold/30 group-hover:border-brand-gold transition-colors duration-300" data-id="element-299" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-brand-gold/30 group-hover:border-brand-gold transition-colors duration-300" data-id="element-300" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-brand-gold/30 group-hover:border-brand-gold transition-colors duration-300" data-id="element-301" />

              {/* Hover Fill Glow */}
              <div className="absolute inset-0 bg-brand-gold/0 group-hover:bg-brand-gold/5 transition-colors duration-500" data-id="element-302" />

              {/* Sweeping Light Reflection */}
              <div className="absolute top-0 left-[-100%] w-[50%] h-full bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 group-hover:left-[200%] transition-all duration-1000 ease-in-out" data-id="element-303" />

              <div className="relative flex items-center justify-center gap-4" data-id="element-304">
                <img src="https://flagcdn.com/w40/es.png" alt="Spain Flag" className="w-8 h-6 object-cover rounded-sm shadow-[0_0_8px_rgba(255,255,255,0.2)]" data-id="element-305" />
                <span className="uppercase tracking-[0.2em] text-sm font-medium text-gray-400 group-hover:text-white transition-colors duration-300" data-id="element-306">
                  Español
                </span>
              </div>
            </motion.button>
          </div>
        </motion.div>

        {/* Bottom Contact Info */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 3.2,
        duration: 0.8
      }} className="mt-20 text-gray-500 text-xs md:text-sm tracking-wider flex flex-col items-center gap-2" data-id="element-307">
          <div className="flex items-center gap-2" data-id="element-308">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-309" />
            <p data-id="element-310">Licensed Public Adjusters — Available 24/7</p>
          </div>
          <p className="font-mono text-gray-400" data-id="element-311">
            (305) 417-0530 <span className="text-brand-gold mx-2" data-id="element-312">|</span> (305)
            785-7829
          </p>
        </motion.div>
      </div>

      {/* Cinematic Top/Bottom Borders */}
      <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-brand-red to-transparent opacity-50" data-id="element-313" />
      <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-50" data-id="element-314" />
    </div>;
}